import React, { useMemo } from 'react';
import times from 'lodash/times';
import classnames from 'classnames';
import styles from './Pagination.module.scss';
import { getTemplateString } from 'common/helpers/string';

interface Props {
  currentPage: number;
  numberOfPages: number;
  numberOfPagesToDisplay?: number;
  numberOfTotalTransactions: number;
  resultsText?: string;
  onChange: (currentPage: number) => void;
  rowCount: number;
}

const getPages = (
  pagesCount: number,
  pagesToDisplay: number = 5,
  currentPage: number = 0,
  offsetBy: number = 2,
) => {
  if (pagesCount >= pagesToDisplay) {
    return times(pagesToDisplay, (i: number) => {
      if (
        currentPage > offsetBy &&
        currentPage <= pagesCount - pagesToDisplay
      ) {
        return i + currentPage - offsetBy;
      }
      if (currentPage > offsetBy && currentPage > pagesCount - pagesToDisplay) {
        return i + (pagesCount - pagesToDisplay) + 1;
      }
      return i + 1;
    });
  } else {
    return times(pagesCount, (i) => i + 1);
  }
};

const getResults = (page: number, total: number, rowCount: number) => {
  const pageCount = Math.round(total / rowCount);
  const isLastPage = page === pageCount;
  const diff = isLastPage ? total % rowCount : rowCount;
  // TransactionHistoryTablePageInfo: "Transaction {RowNumberStart} to {RowNumberEnd} out of {TotalRows}"
  const startRange = isLastPage ? total - diff : page * rowCount - diff;
  const endRange = isLastPage ? total : page * rowCount;
  return {
    RowNumberStart: startRange + 1,
    RowNumberEnd: endRange,
    TotalRows: total,
  };
};

const Pagination = ({
  currentPage = 1,
  numberOfPages,
  numberOfPagesToDisplay,
  numberOfTotalTransactions,
  resultsText,
  onChange,
  rowCount,
}: Props) => {
  const pages = useMemo(
    () =>
      numberOfPages &&
      getPages(numberOfPages, numberOfPagesToDisplay, currentPage),
    [numberOfPages, currentPage],
  );
  const resultsString = getTemplateString(resultsText!);
  const resultsValue = useMemo(
    () => getResults(currentPage, numberOfTotalTransactions, rowCount),
    [currentPage, numberOfTotalTransactions],
  );

  if (numberOfPages) {
    return (
      <>
        <div className={styles.Pagination}>
          <div className={styles.controls}>
            <button
              className={classnames(styles.controlBtn, styles.goFirstBtn)}
              onClick={() => onChange(1)}
            >
              {'<<'}
            </button>
            <button
              className={classnames(styles.controlBtn, styles.prevBtn)}
              onClick={() => currentPage > 1 && onChange(currentPage - 1)}
            >
              {'<'}
            </button>
          </div>
          <div className={styles.pages}>
            {(pages || []).map((index: number) => {
              return (
                <button
                  className={classnames(
                    styles.index,
                    index === currentPage && styles.activeIndex,
                  )}
                  onClick={() => onChange(index)}
                >
                  {index}
                </button>
              );
            })}
          </div>
          <div className={styles.controls}>
            <button
              className={classnames(styles.controlBtn, styles.nextBtn)}
              onClick={() =>
                currentPage < numberOfPages && onChange(currentPage + 1)
              }
            >
              {'>'}
            </button>
            <button
              className={classnames(styles.controlBtn, styles.goLastBtn)}
              onClick={() => onChange(numberOfPages)}
            >
              {'>>'}
            </button>
          </div>
        </div>
        {resultsString && (
          <div className={styles.results}>{resultsString(resultsValue)}</div>
        )}
      </>
    );
  }
  return null;
};

export default Pagination;
